import { dehydrate } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { GetStaticPropsContext } from "next/types";
import { BreadcrumbJsonLd } from "next-seo";
import { useEffect, useRef } from "react";
import { Breadcrumb } from "@/components/Breadcrumb";
import ContainerBoundary from "@/components/common/ContainerBoundary";
import MoreNews from "@/components/sport/MoreNews";
import SportSeo from "@/components/sport/SportSeo";
import { NEWS_PAGE_VIEW } from "@/constants/amplitude";
import { SEO } from "@/constants/seo";
import { GenericSport } from "@/graphql/gql/graphql";
import { BreadcrumbSegment } from "@/types/breadcrumbs";
import { getAvailableSports, getRedirect } from "@/utils/api";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useBreadcrumbs } from "@/utils/hooks/useBreadcrumbs";
import useRecentArticles from "@/utils/hooks/useRecentArticles";
import { findSportIfSportSlug } from "@/utils/index";
import logger from "@/utils/logger";
import { resolveCmsRedirectResponse } from "@/utils/redirects";
import { ProgrammaticCollectionJsonLd } from "@/utils/seo";
import { loadHeader, loadRecentArticles } from "@/utils/ssr";
import styles from "./news.module.scss";

interface SportsNewsProps {
  sport: Partial<GenericSport>;
}

const SportsNews = ({ sport }: SportsNewsProps) => {
  const { setPage, logAmplitudeEvent, isActive } = useAmplitude();
  const pathname = usePathname();
  const {
    articles: moreArticles,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
  } = useRecentArticles({ limit: 12, sportName: sport.name });
  const titleRef = useRef("");
  const title = sport.seo?.title || sport.name;

  const breadcrumbSegments: BreadcrumbSegment[] = [
    { name: (sport.name ?? "").toUpperCase(), path: `/${sport.slug ?? ""}` },
    { name: `${sport.name ?? ""} News`, path: pathname },
  ];
  const { trail, itemListElements } = useBreadcrumbs(breadcrumbSegments);

  useEffect(() => {
    if (titleRef.current !== title && isActive) {
      titleRef.current = title || "";
      setPage("league_news");
      logAmplitudeEvent(NEWS_PAGE_VIEW, { Competition: sport.name || "" });
    }
  }, [setPage, title, sport.name, logAmplitudeEvent, isActive]);

  const seoUrl = `${SEO.FANDUEL_URL_RESEARCH}/${sport.slug}/news`;

  return (
    <>
      <SportSeo sport={sport} definedUrl={seoUrl} />
      <ProgrammaticCollectionJsonLd
        slug={sport.slug ?? ""}
        sub="news"
        name={`${sport.name} news and analysis | FanDuel Research`}
        sportName={sport.name}
        articles={moreArticles}
      />
      <BreadcrumbJsonLd itemListElements={itemListElements} />
      <ContainerBoundary legacy>
        <Breadcrumb trail={trail} />
        <h1 className={styles.heading}>{`${sport.name} News`}</h1>
        <div className={styles.colsContainer}>
          <section>
            {moreArticles && (
              <MoreNews
                hasMore={!!hasNextPage}
                loadMore={fetchNextPage}
                articles={moreArticles}
                origin="sport_news_page"
                isLoading={isLoading || isFetching}
              />
            )}
          </section>
        </div>
      </ContainerBoundary>
    </>
  );
};

export async function getServerSideProps({ params }: GetStaticPropsContext) {
  try {
    const pageSlug = params?.slug?.toString() ?? "";
    const pageSlugLowerCase = pageSlug.toLocaleLowerCase();
    if (pageSlug !== pageSlugLowerCase) {
      return {
        redirect: {
          destination: `/${pageSlugLowerCase}/news`,
          permanent: true,
          // statusCode: 301
        },
      };
    }

    const [redirectResponse, menuClient, availableSportsResponse] =
      await Promise.all([
        getRedirect(`${pageSlugLowerCase}/news`).catch((err) => {
          logger.error("[slug]/news getRedirect", err);
          return undefined;
        }),
        loadHeader(),
        getAvailableSports(),
      ]);
    const cmsRedirectResponse = resolveCmsRedirectResponse(
      redirectResponse?.getRedirect,
      `/${pageSlugLowerCase}/news`
    );
    if (cmsRedirectResponse) {
      return {
        redirect: cmsRedirectResponse,
      };
    }

    const selectedSport = findSportIfSportSlug(
      availableSportsResponse.getSupportedSports,
      pageSlugLowerCase
    );
    if (!selectedSport) {
      return {
        notFound: true,
      };
    }

    const moreArticlesClient = await loadRecentArticles(menuClient, {
      limit: 12,
      sportName: selectedSport.name,
    });

    return {
      props: {
        dehydratedState: dehydrate(moreArticlesClient),
        sport: selectedSport,
      },
    };
  } catch (err) {
    logger.error("Error: [slug]/news getServerSideProps", err);
    return { notFound: true };
  }
}

export default SportsNews;
